/**
 * EventSection — Delegate rodeo list nested-discipline row.
 *
 * Purpose
 *   - Render a single discipline of a rodeo from the unified `events`
 *     table. The contestant pages and admin "My Rodeos" page both read
 *     from this same source. Round configs for the discipline live on
 *     `rodeo_round_configs` keyed by `event_id` (= `events.id`).
 *   - Expanded body lists the discipline's performances. They are
 *     resolved through `performance_events` keyed by `event_id`, so the
 *     visible list matches the contestant view.
 *   - Pencil icon opens the Edit Event dialog INLINE (no navigation to
 *     the rodeo wizard). Uses the shared RodeoEventDialog so the event
 *     details/rounds/prizes UX matches the wizard.
 *
 * Placement
 *   - Rendered by `RodeoListItem` inside its expanded body.
 *   - Leaves performance-level edit/delete flows to the existing
 *     `PerformanceSection`, which handles the inner perf row.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Trophy, DollarSign, Target, Pencil, Trash2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminAddContestantDialog } from '@/components/admin/AdminAddContestantDialog';
import {
  EventPrizeDisplayToggles,
  type EventPrizeDisplayTogglesValues,
} from '@/components/delegate/EventPrizeDisplayToggles';
import { useEventPrize } from '@/api/extensions/usePrizeOps';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePerformancesByEvent } from '@/hooks/usePerformancesByEvent';
import { useRoundConfigsByEvent } from '@/hooks/useRoundConfigsByEvent';
import { useDeleteEvent } from '@/hooks/useDeleteEvent';
import { PerformanceSection } from '@/components/delegate/PerformanceSection';
import { EventDetailsPanel } from '@/components/delegate/EventDetailsPanel';
import { EventDraftSummary } from '@/components/delegate/EventDraftSummary';
import {
  RodeoEventDialog,
  type RodeoEventDialogValues,
} from '@/components/rodeo-wizard/RodeoEventDialog';
import {
  buildEventDialogInitialValues,
  useSaveRodeoEventConfig,
} from '@/features/prize-management/eventConfigPersistence';
import { useSaveEventPrizeToggles } from '@/features/prize-management/prizeDisplayToggles';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import {
  useEventsClient,
  eventsKeys,
} from '@/api/generated_hooks/generated-events-hooks';
import {
  usePerformanceEventsClient,
  performanceEventsKeys,
} from '@/api/generated_hooks/generated-performance_events-hooks';
import {
  usePerformancesClient,
  performancesKeys,
} from '@/api/generated_hooks/generated-performances-hooks';

export interface EventSectionProps {
  /**
   * `events` row from the unified read path. Known fields consumed:
   *   - id, event_name / trig_event_type_name (display label)
   *   - entry_fee, transaction_fee, total_prize_amount, max_contestants
   *   - metadata.event_category, metadata.prizes.total_prize_amount
   *     (legacy fallback)
   */
  event: any;
  /** Parent rodeo id — forwarded to the nested PerformanceSection. */
  rodeoId: string;
  /**
   * Optional parent rodeo row. When provided, the expanded details panel
   * surfaces rodeo-level context (dates, location, host org, etc.) so the
   * delegate can decide whether to open Edit Event without scrolling back
   * up to the rodeo header. Old callers that only pass `rodeoId` still
   * work — the rodeo section of the panel is simply omitted.
   */
  rodeo?: any;
}

export function EventSection({ event, rodeoId, rodeo }: EventSectionProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  // Manual "Add Contestant" admin dialog — same component the legacy
  // jackpot draw page uses, surfaced here so rodeo admins have a
  // post-close entry path without leaving My Rodeos. Admin-gated.
  const [addContestantOpen, setAddContestantOpen] = useState(false);
  const role = useAuthStore((s) => s.role);
  const isAdmin = role?.isAdmin === true || role?.role_name === 'admin';
  // Delete-event confirm dialog state. The destructive flow lives on this
  // surface to mirror the trash button on the parent rodeo + child perfs —
  // those use their own confirm modals too.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteEventMutation = useDeleteEvent();

  // event.id is the canonical identifier and matches `rodeo_round_configs.event_id`.
  // Discipline label preferring the trigger-denormalised type, then the
  // legacy fallback where rodeo events stored the discipline in event_name.
  const eventTypeLabel: string =
    event?.trig_event_type_name || event?.event_name || '';
  // Custom display name — only meaningful when distinct from the discipline.
  const customEventName: string =
    typeof event?.event_name === 'string' &&
    event.event_name.trim().length > 0 &&
    event.event_name !== event?.trig_event_type_name
      ? event.event_name
      : '';

  // Gate the performances query on expand — collapsed rows stay silent.
  const { data: unifiedPerformances, isLoading } = usePerformancesByEvent(
    event?.id,
    { enabled: expanded && !!event?.id },
  );

  // Project the unified `performances` rows into the shape
  // PerformanceSection consumes. We synthesize the date/time fields from
  // start_datetime; p.id matches the wizard-side id directly.
  const performances = useMemo(() => {
    return (unifiedPerformances ?? []).map((p: any, idx: number) => {
      const sd = p.start_datetime ? new Date(p.start_datetime) : null;
      return {
        id: p.id,
        rodeo_id: p.trig_rodeo_id ?? rodeoId,
        performance_name: p.performance_name,
        performance_date: sd ? sd.toISOString().slice(0, 10) : null,
        start_time: sd ? sd.toISOString().slice(11, 19) : null,
        end_time: null,
        sort_order: idx,
        is_slack: p.is_slack ?? false,
        default_max_contestants: p.max_contestants ?? null,
        // PerformanceSection's prop is typed against PerformancesRow
        // which carries metadata + created/updated_by. None of those
        // fields are read by the component, but TypeScript demands them
        // present, so synthesize from the unified row.
        metadata: '{}',
        created_by: p.created_by ?? null,
        created_at: p.created_at ?? new Date().toISOString(),
        updated_by: p.updated_by ?? null,
        updated_at: p.updated_at ?? new Date().toISOString(),
      };
    });
  }, [unifiedPerformances, rodeoId]);

  // Rounds are needed whenever the dialog opens (to hydrate the rounds
  // step). Keep the query enabled while the dialog is open; React Query
  // caches it across open/close cycles. event.id matches
  // `rodeo_round_configs.event_id` (FK to events).
  const { data: existingRounds } = useRoundConfigsByEvent(
    dialogOpen ? event?.id : undefined,
  );

  const performanceEventsClient = usePerformanceEventsClient();
  const performancesClient = usePerformancesClient();
  const eventsClient = useEventsClient();
  const { saveEventConfig } = useSaveRodeoEventConfig();
  const { saveEventToggles } = useSaveEventPrizeToggles();

  // All performances on the parent rodeo — populates the dialog's per-perf
  // toggle list. Filtered server-side via `trig_rodeo_id` (the trigger-
  // denormalised rodeo pointer on `performances`). Gated on dialog open so
  // the request only fires when the user actually opens Edit Event.
  const { data: rodeoPerfs } = useQuery({
    queryKey: performancesKeys.list({ trig_rodeo_id: rodeoId }),
    queryFn: async () => {
      const res = await performancesClient.getAll({
        filters: { trig_rodeo_id: rodeoId },
      } as any);
      return Array.isArray(res) ? res : ((res as any)?.data ?? []);
    },
    enabled: dialogOpen && !!rodeoId,
  });

  // Sibling events on the same rodeo — feeds `usedEventTypes` so the
  // event-type select greys out disciplines another event already owns.
  // The dialog excludes the editing event itself, so the user can keep
  // the current event's type selected.
  const { data: rodeoEvents } = useQuery({
    queryKey: eventsKeys.list({ rodeo_id: rodeoId }),
    queryFn: async () => {
      const res = await eventsClient.getAll({
        filters: { rodeo_id: rodeoId },
      } as any);
      return Array.isArray(res) ? res : ((res as any)?.data ?? []);
    },
    enabled: dialogOpen && !!rodeoId,
  });

  // Junction rows for THIS event — drives the toggle's initial checked
  // state in the edit dialog AND the "X performances" count rendered in
  // the collapsed header. The header count needs to be correct on first
  // render (without requiring expansion or opening the dialog), so this
  // query stays enabled whenever the row has mounted. EventSection only
  // mounts when the parent rodeo is expanded, so the fetch fan-out is
  // already bounded by that. The cache is reused when the dialog opens.
  const { data: existingJoins } = useQuery({
    queryKey: event?.id
      ? performanceEventsKeys.list({ event_id: event.id })
      : performanceEventsKeys.lists(),
    queryFn: async () => {
      const res = await performanceEventsClient.getAll({
        filters: { event_id: event!.id },
      } as any);
      return Array.isArray(res) ? res : ((res as any)?.data ?? []);
    },
    enabled: !!event?.id,
  });


  // Display values come straight from the events row. The legacy
  // metadata.prizes.total_prize_amount path is honoured for older rows
  // that pre-date `events.total_prize_amount` getting written from the
  // wizard.
  const totalPrize = Number(
    event?.total_prize_amount ??
      (event?.metadata as any)?.prizes?.total_prize_amount ??
      0,
  );
  const entryFee = Number(event?.entry_fee ?? 0);
  const txnFee = Number(event?.transaction_fee ?? 0);
  const eventCategory: 'timed' | 'scored' | undefined =
    (event?.metadata as any)?.event_category;

  // Live computed prize pool — the canonical engine applies the org-default +
  // custom adjustments and, for dynamic allocation, multiplies the per-entry
  // contribution by the current active-registration count. The raw
  // `total_prize_amount` above is the pre-adjustment base (and is 0 for dynamic
  // events), so the header/detail "prize pool" figure prefers this net total.
  const { data: prizeComputation } = useEventPrize(event?.id, {
    enabled: !!event?.id,
  });
  const isDynamicPrize = (event as any)?.prize_allocation_mode === 'dynamic';
  // Fall back to the static base only while the compute request is in flight.
  const netPrizePool = prizeComputation?.calculation.total ?? totalPrize;

  // ── Per-event prize-display toggles ──────────────────────────────────────
  // Same three switches as the Edit Event dialog, surfaced inline. Persisted
  // directly to the `events` row via the generated update hook with an
  // optimistic local state; the dependency invariant (before-complete requires
  // in-results) is enforced in the toggle component before it reaches us.
  const { showError } = useToast();
  const eventToggleValues: EventPrizeDisplayTogglesValues = useMemo(
    () => ({
      display_prize_to_contestants: !!(event as any)?.display_prize_to_contestants,
      show_prize_amounts_in_results:
        (event as any)?.show_prize_amounts_in_results !== false,
      show_prize_amounts_before_complete:
        !!(event as any)?.show_prize_amounts_before_complete,
    }),
    [event],
  );
  const [toggleValues, setToggleValues] =
    useState<EventPrizeDisplayTogglesValues>(eventToggleValues);
  const [togglesSaving, setTogglesSaving] = useState(false);
  // Re-sync local state when the event row refetches (e.g. after the dialog
  // saves, or another surface flips a toggle).
  useEffect(() => {
    setToggleValues(eventToggleValues);
  }, [eventToggleValues]);

  const handleToggleChange = async (next: EventPrizeDisplayTogglesValues) => {
    if (!event?.id) return;
    const prev = toggleValues;
    setToggleValues(next); // optimistic
    setTogglesSaving(true);
    try {
      await saveEventToggles(event.id, next);
    } catch (err) {
      setToggleValues(prev); // revert on failure
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to update prize display settings. Please try again.';
      showError('Could not save toggle', message);
    } finally {
      setTogglesSaving(false);
    }
  };

  // Fall back to the event's first performance for any new round rows
  // the user adds (performance_id would otherwise be null → NOT NULL
  // constraint on the round_configs FK would reject it).
  const fallbackPerfId =
    performances && performances.length > 0 ? performances[0].id : null;

  // Hydrated from the shared module so this surface and the Prize Management
  // tab feed the dialog identical values.
  const initialValues: RodeoEventDialogValues = useMemo(
    () => buildEventDialogInitialValues(event, existingRounds as any, existingJoins as any),
    [event, existingRounds, existingJoins],
  );

  // Save goes through the shared persistence path (events row → round-config
  // clean-slate resync → performance_events diff). Same code the Prize
  // Management tab runs, so the two surfaces cannot write different rows.
  const handleEditSubmit = async (values: RodeoEventDialogValues) => {
    setEditError(null);
    setIsSavingEvent(true);
    try {
      await saveEventConfig({
        event,
        rodeoId,
        values,
        existingRounds: existingRounds as any,
        existingJoins: existingJoins as any,
        fallbackPerformanceId: fallbackPerfId,
      });
      setDialogOpen(false);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save event. Please try again.';
      setEditError(message);
    } finally {
      setIsSavingEvent(false);
    }
  };

  // Delete-event flow. Errors stay in the modal so the user sees them and
  // can retry; success closes the modal and invalidations remove this row.
  const handleDeleteConfirm = async () => {
    if (!event?.id) return;
    setDeleteError(null);
    try {
      await deleteEventMutation.mutateAsync(event.id);
      setShowDeleteConfirm(false);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to delete event. Please try again.';
      setDeleteError(message);
    }
  };

  return (
    <div className="rounded-md border border-border/60 bg-background overflow-hidden">
      {/* Clickable header — same chevron + hover treatment as RodeoListItem. */}
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer select-none bg-muted/40 hover:bg-muted transition-colors border-l-2 border-border"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Trophy className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="font-medium text-sm truncate">
              {getEventDisplayTitle(event, 'Unnamed event')}
            </span>
            {customEventName && eventTypeLabel && (
              <span className="text-xs text-muted-foreground truncate">
                · {eventTypeLabel}
              </span>
            )}
            {eventCategory && (
              <span className="text-xs text-muted-foreground capitalize">
                {eventCategory}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
            {entryFee > 0 && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                ${entryFee} entry
                {txnFee > 0 ? ` + $${txnFee} txn` : ''}
              </span>
            )}
            {(netPrizePool > 0 || isDynamicPrize) && (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                <Trophy className="h-3 w-3" />
                ${netPrizePool.toLocaleString(undefined, { maximumFractionDigits: 0 })} prize pool
                {isDynamicPrize ? ' (live)' : ''}
              </span>
            )}
            {(() => {
              // Source of truth for the assigned-perf count is the
              // performance_events junction (always loaded above).
              // When the event row is expanded we already have the
              // hydrated performance list, so prefer its length to
              // stay in lockstep with what's rendered below.
              const perfCount = expanded
                ? (performances?.length ?? existingJoins?.length ?? 0)
                : (existingJoins?.length ?? 0);
              return (
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  {perfCount} {perfCount === 1 ? 'performance' : 'performances'}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Inline prize-display toggles + direct-edit pencil + delete trash.
            stopPropagation so toggling a switch or clicking an action doesn't
            also expand/collapse the accordion row. */}
        <div
          className="flex items-center gap-3 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Compact, always-visible prize-display switches — same three the
              Edit Event dialog exposes, so a delegate can flip them per event
              without expanding the row. */}
          {event?.id && (
            <EventPrizeDisplayToggles
              idPrefix={`evt-${event.id}`}
              values={toggleValues}
              onChange={handleToggleChange}
              disabled={togglesSaving}
            />
          )}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Edit event"
              onClick={() => {
                setEditError(null);
                setDialogOpen(true);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete event"
              onClick={() => {
                setDeleteError(null);
                setShowDeleteConfirm(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded body — details panel + performances list. The panel surfaces
          most of the event + rodeo configuration in a compact grid so a delegate
          can decide whether the event needs editing without opening the dialog. */}
      {expanded && (
        <div className="px-3 py-2 space-y-3 border-l-2 border-border bg-background">
          <EventDetailsPanel
            event={event}
            rodeo={rodeo}
            prizePoolOverride={prizeComputation ? netPrizePool : undefined}
          />

          {/* Admin-only manual entry. Surfaced here so admins can add a
              contestant (including post-close) directly from the rodeo
              event accordion — same dialog the legacy jackpot draw page
              mounts. Closing refetches the event-registrations query so
              the new entry shows up immediately in any list that's
              already loaded. */}
          {isAdmin && event?.id && (
            <div className="flex items-center justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddContestantOpen(true)}
              >
                <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                Add Contestant
              </Button>
            </div>
          )}
          {isAdmin && event?.id && (
            <AdminAddContestantDialog
              eventId={event.id}
              open={addContestantOpen}
              onOpenChange={(open) => {
                setAddContestantOpen(open);
                if (!open) {
                  // Invalidate everything keyed off this event so the
                  // draft summary + any visible registrations list
                  // catch the new row without a manual refresh.
                  queryClient.invalidateQueries({ queryKey: ['draw-ops'] });
                  queryClient.invalidateQueries({ queryKey: ['registration-ops'] });
                  queryClient.invalidateQueries({ queryKey: ['event_registrations'] });
                }
              }}
            />
          )}

          <div className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Performances
            </h4>
            {isLoading ? (
              <p className="text-sm text-muted-foreground italic">
                Loading performances…
              </p>
            ) : performances && performances.length > 0 ? (
              performances.map((perf) => (
                <PerformanceSection
                  key={perf.id}
                  performance={perf as any}
                  rodeoId={rodeoId}
                  expandable={false}
                />
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No performances added yet.
              </p>
            )}
          </div>

          {/* Draft draw summary — surfaces the saved draft (or published)
              placement broken down per performance, with a deep link into
              the full draw screen. Read-only and admin-gated (the draft
              endpoint is admin-only); delegates see a notice + deep link
              instead of an attempted fetch. */}
          {event?.id && rodeoId && (
            <EventDraftSummary
              eventId={event.id}
              eventName={getEventDisplayTitle(event, 'Event')}
              rodeoId={rodeoId}
            />
          )}
        </div>
      )}

      {/* Inline Edit Event dialog — same shared component used by the
          rodeo wizard + nested PerformanceSection paths. The Performances
          toggle list is now wired through this surface too: rodeoPerfs
          drives the option list and the dialog's `performance_assignments`
          changes are persisted via `performance_events` in handleEditSubmit. */}
      <RodeoEventDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditError(null);
        }}
        mode="edit"
        initialValues={initialValues}
        performances={(rodeoPerfs ?? []).map((p: any) => {
          // Synthesize the perf-date / start-time fields from the unified
          // `start_datetime` so the dialog's subtitle matches the rest of
          // the delegate UI ("Fri May 7 · 7:00 PM").
          const sd = p.start_datetime ? new Date(p.start_datetime) : null;
          return {
            key: p.id,
            name: p.performance_name ?? '',
            performance_date: sd ? sd.toISOString().slice(0, 10) : null,
            start_time: sd ? sd.toISOString().slice(11, 19) : null,
            // No sibling-event chips on this surface — would require a
            // second fan-out fetch and isn't load-bearing now that the
            // dialog allows multi-event-per-perf.
            assigned_event_types: [],
            default_max_contestants: p.max_contestants ?? null,
          };
        })}
        // Rodeo-wide uniqueness on event_type — the dialog disables any
        // type already claimed by a sibling event so the user can't end
        // up with two "Team Roping" configs on one rodeo. The current
        // event is excluded so its own type stays selectable.
        usedEventTypes={(rodeoEvents ?? [])
          .filter((ec: any) => ec.id !== event?.id)
          .map((ec: any) => ec.trig_event_type_name ?? ec.event_type ?? '')
          .filter(Boolean)}
        onSubmit={handleEditSubmit}
        organizationId={(rodeo as any)?.primary_organization_id ?? null}
        errorMessage={editError}
        isSaving={isSavingEvent}
      />

      {/* Delete-event confirmation — destructive flow goes through
          useDeleteEvent which wipes the performance_events junction first
          and then deletes the event row. The cache invalidation in that
          hook removes this component from the parent list on success. */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeleteConfirm(false);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-destructive px-1" role="alert">
              {deleteError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteError(null);
              }}
              disabled={deleteEventMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteEventMutation.isPending}
            >
              {deleteEventMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EventSection;
